import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Hook centralizzato per la gestione delle notifiche e degli errori
 * Fornisce un'interfaccia unificata per gestire:
 * - Messaggi di successo
 * - Messaggi di errore
 * - Messaggi informativi
 * - Messaggi di warning
 */
export const useNotifications = () => {
    const { t } = useTranslation('global');
    
    // Stati per i diversi tipi di notifiche
    const [notification, setNotification] = useState({
        success: null,
        error: null,
        info: null,
        warning: null
    });

    // Funzione per pulire tutte le notifiche
    const clearAll = useCallback(() => {
        setNotification({
            success: null,
            error: null,
            info: null,
            warning: null
        });
    }, []);

    // Funzione per pulire una notifica specifica
    const clear = useCallback((type) => {
        setNotification(prev => ({
            ...prev,
            [type]: null
        }));
    }, []);

    // Funzioni per mostrare notifiche specifiche
    const showSuccess = useCallback((message, duration = 6000) => {
        setNotification(prev => ({
            ...prev,
            success: { message, duration, timestamp: Date.now() }
        }));
    }, []);

    const showError = useCallback((error, duration = 8000) => {
        let message;
        
        // Gestione intelligente degli errori
        if (typeof error === 'string') {
            message = error;
        } else if (error?.response?.data?.message) {
            message = error.response.data.message;
        } else if (error?.response?.data?.error) {
            message = error.response.data.error;
        } else if (error?.message) {
            message = error.message;
        } else if (error?.response?.status) {
            // Gestione errori HTTP standard
            switch (error.response.status) {
                case 400:
                    message = t('errors.badRequest', 'Richiesta non valida');
                    break;
                case 401:
                    message = t('errors.unauthorized', 'Accesso non autorizzato');
                    break;
                case 403:
                    message = t('errors.forbidden', 'Accesso vietato');
                    break;
                case 404:
                    message = t('errors.notFound', 'Risorsa non trovata');
                    break;
                case 408:
                    message = t('errors.timeout', 'Timeout della richiesta');
                    break;
                case 409:
                    message = t('errors.conflict', 'Conflitto con i dati esistenti');
                    break;
                case 422:
                    message = t('errors.validation', 'Errore di validazione dei dati');
                    break;
                case 429:
                    message = t('errors.tooManyRequests', 'Troppe richieste. Riprova più tardi');
                    break;
                case 500:
                    message = t('errors.serverError', 'Errore interno del server');
                    break;
                case 502:
                    message = t('errors.badGateway', 'Errore del gateway');
                    break;
                case 503:
                    message = t('errors.serviceUnavailable', 'Servizio temporaneamente non disponibile');
                    break;
                case 504:
                    message = t('errors.gatewayTimeout', 'Timeout del gateway');
                    break;
                default:
                    message = t('errors.generic', 'Si è verificato un errore. Riprova più tardi');
            }
        } else if (error?.code === 'ERR_NETWORK') {
            message = t('errors.network', 'Impossibile connettersi al server');
        } else if (error?.code === 'ECONNABORTED') {
            message = t('errors.timeout', 'Timeout della connessione');
        } else {
            message = t('errors.unknown', 'Errore sconosciuto');
        }

        setNotification(prev => ({
            ...prev,
            error: { message, duration, timestamp: Date.now() }
        }));
    }, [t]);

    const showInfo = useCallback((message, duration = 5000) => {
        setNotification(prev => ({
            ...prev,
            info: { message, duration, timestamp: Date.now() }
        }));
    }, []);

    const showWarning = useCallback((message, duration = 7000) => {
        setNotification(prev => ({
            ...prev,
            warning: { message, duration, timestamp: Date.now() }
        }));
    }, []);

    // Funzioni di utilità per operazioni comuni
    const handleApiError = useCallback((error, customMessage = null) => {
        console.error('API Error:', error);
        showError(customMessage || error);
    }, [showError]);

    const handleApiSuccess = useCallback((message = null) => {
        showSuccess(message || t('common.operationSuccess', 'Operazione completata con successo'));
    }, [showSuccess, t]);

    // Funzioni per gestire operazioni asincrone con loading
    const [loading, setLoading] = useState(false);

    const executeWithNotification = useCallback(async (
        asyncOperation,
        {
            loadingMessage = null,
            successMessage = null,
            errorMessage = null,
            showLoading = true
        } = {}
    ) => {
        try {
            if (showLoading) {
                setLoading(true);
                if (loadingMessage) {
                    showInfo(loadingMessage);
                }
            }
            
            const result = await asyncOperation();
            
            if (successMessage) {
                showSuccess(successMessage);
            }
            
            return result;
        } catch (error) {
            handleApiError(error, errorMessage);
            throw error; // Re-throw per permettere gestione specifica se necessaria
        } finally {
            if (showLoading) {
                setLoading(false);
                clear('info'); // Pulisce il messaggio di loading
            }
        }
    }, [showInfo, showSuccess, handleApiError, clear]);

    // Stati calcolati
    const hasNotifications = useMemo(() => {
        return notification.success || notification.error || notification.info || notification.warning;
    }, [notification]);

    const activeNotifications = useMemo(() => {
        const active = [];
        Object.entries(notification).forEach(([type, data]) => {
            if (data) {
                active.push({ type, ...data });
            }
        });
        return active;
    }, [notification]);

    return {
        // Stati delle notifiche
        notification,
        hasNotifications,
        activeNotifications,
        loading,
        
        // Funzioni per mostrare notifiche
        showSuccess,
        showError,
        showInfo,
        showWarning,
        
        // Funzioni per pulire notifiche
        clear,
        clearAll,
        
        // Funzioni di utilità
        handleApiError,
        handleApiSuccess,
        executeWithNotification
    };
};

/**
 * Hook semplificato per gestione errori base
 * Utile per componenti che necessitano solo di gestione errori/successi semplici
 */
export const useSimpleNotifications = () => {
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [loading, setLoading] = useState(false);
    
    const clearError = useCallback(() => setError(null), []);
    const clearSuccess = useCallback(() => setSuccess(null), []);
    const clearAll = useCallback(() => {
        setError(null);
        setSuccess(null);
    }, []);
    
    return {
        error,
        success,
        loading,
        setError,
        setSuccess,
        setLoading,
        clearError,
        clearSuccess,
        clearAll
    };
};

export default useNotifications;